import { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Sparkles, ArrowLeft, Feather, Scale, Zap, X } from 'lucide-react'
import toast from 'react-hot-toast'
import apiClient from '../services/api'
import { apiErrorDetail } from '../utils/dataValidator'

interface Project {
  id: string
  name: string
}

interface ScopeOption {
  type: 'project' | 'collection' | 'bucket' | 'cluster'
  ref_id: number | null
  label: string
}

type DraftStyle = 'conservative' | 'balanced' | 'compressed'
type ScopeType = 'project' | 'collection' | 'bucket' | 'cluster'

function parseScopeType(raw: string | null): ScopeType {
  return raw === 'collection' || raw === 'bucket' || raw === 'cluster' ? raw : 'project'
}

export default function CreateAiDraftPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const prefillProjectId = searchParams.get('project_id') || ''
  const prefillScopeType = parseScopeType(searchParams.get('scope_type'))
  const prefillScopeRefIdRaw = searchParams.get('scope_ref_id')
  const prefillScopeRefId = prefillScopeRefIdRaw ? Number(prefillScopeRefIdRaw) : null

  const [projects, setProjects] = useState<Project[]>([])
  const [projectsLoading, setProjectsLoading] = useState(true)
  const [targetProjectId, setTargetProjectId] = useState(prefillProjectId)
  const [newName, setNewName] = useState('')
  const [newStyle, setNewStyle] = useState<DraftStyle>('conservative')
  const [newScopeType, setNewScopeType] = useState<ScopeType>(prefillScopeType)
  const [newScopeRefId, setNewScopeRefId] = useState<number | null>(
    prefillScopeType !== 'project' ? prefillScopeRefId : null
  )
  const [scopeOptions, setScopeOptions] = useState<ScopeOption[]>([])
  const [scopeLoading, setScopeLoading] = useState(false)
  const [momentsCount, setMomentsCount] = useState<number | null>(null)
  const [momentsLoading, setMomentsLoading] = useState(false)
  const [creating, setCreating] = useState(false)

  const canCreate = Boolean(
    targetProjectId &&
    momentsCount != null && momentsCount > 0 &&
    newName.trim() &&
    !momentsLoading &&
    !creating
  )

  const fetchProjects = useCallback(async () => {
    try {
      setProjectsLoading(true)
      const res = await apiClient.get('/projects/')
      setProjects((res.data.projects || []).map((p: { id: number; name: string }) => ({ id: p.id, name: p.name })))
    } catch {
      toast.error('Failed to load workspaces')
    } finally {
      setProjectsLoading(false)
    }
  }, [])

  const fetchScopeOptions = useCallback(async (projectId: string) => {
    setScopeLoading(true)
    const options: ScopeOption[] = [{ type: 'project', ref_id: null, label: 'All project assets' }]
    try {
      const [colRes, bucRes, cluRes] = await Promise.allSettled([
        apiClient.get('/collections/', { params: { project_id: projectId } }),
        apiClient.get('/buckets/', { params: { project_id: projectId } }),
        apiClient.get('/clusters/', { params: { project_id: projectId } }),
      ])
      if (colRes.status === 'fulfilled') {
        for (const c of colRes.value.data.collections || []) {
          options.push({ type: 'collection', ref_id: c.id, label: `Collection: ${c.name}` })
        }
      }
      if (bucRes.status === 'fulfilled') {
        for (const b of bucRes.value.data.buckets || []) {
          options.push({ type: 'bucket', ref_id: b.id, label: `Bucket: ${b.name}` })
        }
      }
      if (cluRes.status === 'fulfilled') {
        for (const cl of cluRes.value.data.clusters || []) {
          options.push({ type: 'cluster', ref_id: cl.id, label: `Cluster: ${cl.name}` })
        }
      }
    } finally {
      setScopeOptions(options)
      setScopeLoading(false)
    }
  }, [])

  useEffect(() => { fetchProjects() }, [fetchProjects])

  useEffect(() => {
    if (!targetProjectId) {
      setScopeOptions([])
      setScopeLoading(false)
      setMomentsCount(null)
      setMomentsLoading(false)
      return
    }
    fetchScopeOptions(targetProjectId)
  }, [targetProjectId, fetchScopeOptions])

  useEffect(() => {
    if (scopeLoading || !targetProjectId || newScopeType === 'project') return
    const selectedScopeExists = scopeOptions.some(
      opt => opt.type === newScopeType && opt.ref_id === newScopeRefId
    )
    if (!selectedScopeExists) {
      setNewScopeType('project')
      setNewScopeRefId(null)
    }
  }, [scopeLoading, scopeOptions, targetProjectId, newScopeType, newScopeRefId])

  useEffect(() => {
    if (!targetProjectId) {
      setMomentsCount(null)
      setMomentsLoading(false)
      return
    }

    let cancelled = false
    const loadMoments = async () => {
      try {
        setMomentsLoading(true)
        const res = await apiClient.get('/assemblies/preflight', {
          params: {
            project_id: targetProjectId,
            scope_type: newScopeType,
            scope_ref_id: newScopeRefId ?? undefined,
          },
        })
        if (!cancelled) setMomentsCount(res.data.moments_count ?? 0)
      } catch {
        if (!cancelled) setMomentsCount(null)
      } finally {
        if (!cancelled) setMomentsLoading(false)
      }
    }

    loadMoments()
    return () => { cancelled = true }
  }, [targetProjectId, newScopeType, newScopeRefId])

  const createAiDraft = async () => {
    if (!targetProjectId) { toast.error('Select a workspace'); return }
    if (momentsCount == null || momentsCount === 0) {
      toast.error('No moments found in this workspace. Run ingest/analysis first.')
      return
    }
    if (!newName.trim()) { toast.error('Assembly name is required'); return }

    try {
      setCreating(true)
      const body: Record<string, unknown> = {
        name: newName.trim(),
        assembly_type: 'ai_draft',
        style: newStyle,
        scope_type: newScopeType,
      }
      if (newScopeRefId !== null) body.scope_ref_id = newScopeRefId

      const res = await apiClient.post(`/assemblies/?project_id=${targetProjectId}`, body)
      toast.success('AI draft created. Generating...')
      navigate(`/assemblies/${res.data.id}/processing`)
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to create AI draft'))
    } finally {
      setCreating(false)
    }
  }

  return (
    <div className="rq-page">
      <div className="max-w-4xl mx-auto">
        <Link to="/assemblies" className="inline-flex items-center gap-2 text-sm text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] mb-4">
          <ArrowLeft className="w-4 h-4" />
          Back to Assemblies
        </Link>

        <header className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-5 h-5 text-[var(--rq-amber)]" />
            <h1 className="rq-title">Create AI Draft</h1>
          </div>
          <p className="rq-subtitle">Select source footage and create a structured first draft proposal.</p>
        </header>

        <section className="rq-card p-6 md:p-7 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-[var(--rq-text-soft)] mb-2">Workspace</label>
              <select
                value={targetProjectId}
                onChange={e => {
                  const pid = e.target.value
                  setTargetProjectId(pid)
                  setNewScopeType('project')
                  setNewScopeRefId(null)
                }}
                disabled={projectsLoading}
                className="w-full rq-input-dark rounded-lg px-3 py-2.5 text-sm"
              >
                <option value="">Select workspace</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-[var(--rq-text-soft)] mb-2">Scope</label>
              <select
                value={newScopeType === 'project' ? 'project' : `${newScopeType}:${newScopeRefId}`}
                onChange={e => {
                  const val = e.target.value
                  if (val === 'project') {
                    setNewScopeType('project')
                    setNewScopeRefId(null)
                    return
                  }
                  const [type, refId] = val.split(':')
                  setNewScopeType(type as ScopeType)
                  setNewScopeRefId(Number(refId))
                }}
                disabled={scopeLoading || !targetProjectId}
                className="w-full rq-input-dark rounded-lg px-3 py-2.5 text-sm"
              >
                {!targetProjectId ? (
                  <option>Select workspace first</option>
                ) : scopeLoading ? (
                  <option>Loading sources...</option>
                ) : (
                  scopeOptions.map((opt, i) => (
                    <option key={`${opt.type}-${opt.ref_id ?? i}`} value={opt.type === 'project' ? 'project' : `${opt.type}:${opt.ref_id}`}>
                      {opt.label}
                    </option>
                  ))
                )}
              </select>
            </div>
          </div>

          {targetProjectId && (
            <div className="text-sm">
              {momentsLoading ? (
                <span className="text-[var(--rq-text-soft)]">Checking available moments...</span>
              ) : momentsCount === 0 ? (
                <span className="text-red-300">No moments detected in this scope. Analyze footage first.</span>
              ) : (
                <span className="text-[var(--rq-text-soft)]">
                  Available moments: <span className="text-[var(--rq-text)] tabular-nums">{momentsCount ?? '—'}</span>
                </span>
              )}
            </div>
          )}

          <div>
            <p className="text-sm text-[var(--rq-text-soft)] mb-2">Draft Style</p>
            <div className="flex flex-wrap rounded-lg bg-white/5 p-1 gap-1">
              {([
                { key: 'conservative' as const, icon: Feather, label: 'Conservative' },
                { key: 'balanced' as const, icon: Scale, label: 'Balanced' },
                { key: 'compressed' as const, icon: Zap, label: 'Compressed' },
              ]).map(item => (
                <button
                  key={item.key}
                  onClick={() => setNewStyle(item.key)}
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm whitespace-nowrap transition-colors ${
                    newStyle === item.key
                      ? 'bg-[var(--btn)] text-white shadow-sm'
                      : 'text-[var(--rq-text-soft)] hover:text-[var(--rq-text)]'
                  }`}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm text-[var(--rq-text-soft)] mb-2">Assembly Name</label>
            <div className="relative">
              <input
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="Episode 1 - draft v1"
                className="w-full rq-input-dark rounded-lg px-3 py-2.5 pr-9 text-sm"
                onKeyDown={e => {
                  if (e.key === 'Enter' && canCreate) createAiDraft()
                }}
              />
              {newName && (
                <button
                  onClick={() => setNewName('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          <div className="pt-2 border-t border-white/10 flex items-center justify-end gap-3">
            <Link to="/assemblies" className="rq-btn-ghost px-4 py-2 text-sm">
              Cancel
            </Link>
            <button
              onClick={createAiDraft}
              disabled={!canCreate}
              className="rq-btn-primary px-4 py-2 text-sm font-medium disabled:opacity-40"
            >
              <Sparkles className="w-4 h-4" />
              {creating ? 'Creating...' : 'Create AI Draft'}
            </button>
          </div>
        </section>
      </div>
    </div>
  )
}
